import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { CommunityFeedSurveyDto, SurveyDetailsV2Dto } from "@/api/types";
import { Anchor } from "@/components/Anchor/Anchor";
import { Button } from "@/components/Button/Button";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { CountdownLabel } from "@/components/CountdownLabel/CountdownLabel";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { LinkFormatter } from "@/components/LinkFormatter/LinkFormatter";
import { useProject } from "@/hooks/Network/useProject";
import { useBool } from "@/hooks/useBool";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CommunityItem } from "./CommunityItem";

interface CommunitySurveyProps {
  survey: CommunityFeedSurveyDto;
}

export function CommunitySurvey({ survey }: CommunitySurveyProps): React.ReactNode {
  const [isAppSurveyModalOpen, appSurveyModalHandler] = useBool(false);

  const project = useProject();
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  const updateSurveyReadStatusMutation = useMutation({
    mutationFn: () => api.postSurveysReadV1(survey.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.MESSAGES(project.id), "FEED"],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MESSAGES_FEED_STATS(project.id),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("component.community-survey.mark-as-read.error"),
      });
    },
  });
  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("component.community-post.actions.mark-as-read"),
      },
      "data-testid": "post-context-menu-mark-read",
      value: "markAsRead",
      icon: <CheckIcon />,
      type: "button",
      callbackFn: updateSurveyReadStatusMutation.mutate,
      isHidden: !survey.isUnread,
    },
  ];

  const endDate = survey.endsAt ? new Date(survey.endsAt) : undefined;
  const isAutomatedSurvey = survey.scope === "automated";
  let ctaLabel = isAutomatedSurvey
    ? t("component.community-survey.button.start-automated-survey")
    : t("component.community-survey.button.start-standard-survey");
  if (survey.canRespond === "notAllowed") {
    ctaLabel = t("component.community-survey.button.not-allowed");
  } else if (survey.canRespond === "alreadyAnswered") {
    ctaLabel = t("component.community-survey.button.already-answered");
  } else if (survey.canRespond === "notStarted") {
    ctaLabel = t("component.community-survey.button.not-started");
  } else if (survey.canRespond === "ended") {
    ctaLabel = t("component.community-survey.button.ended");
  }

  return (
    <>
      <CommunityItem
        ring={survey.isUnread ? "unread" : undefined}
        user={survey.createdBy}
        group={survey.group}
        actions={menuActions}
        dateSubtitle={survey.startsAt && <FormattedDate format="datetime" date={survey.startsAt} />}
      >
        <div className="flex flex-col gap-4">
          {/* Countdown label */}
          {endDate && <CountdownLabel endDate={endDate} />}

          {/* Content */}
          <p className="whitespace-pre-line text-body-bold">{survey.title}</p>
          <p className="text-body">
            <LinkFormatter>{survey.description}</LinkFormatter>
          </p>

          {/* CTA */}
          <Button
            styling="primary"
            type="link"
            href={routes.surveys.respond({ slug: project.slug, id: survey.id })}
            state={{
              category: (isAutomatedSurvey ? "feelAtHome" : "other") satisfies SurveyDetailsV2Dto["category"],
            }}
            data-testid="community-survey-cta-btn"
            onClick={() => updateSurveyReadStatusMutation.mutate()}
            disabled={survey.canRespond !== "yes"}
          >
            {ctaLabel}
          </Button>
        </div>
      </CommunityItem>

      <ConfirmModal
        title={t("component.community-post.app-survey.modal.title")}
        description={t("component.community-post.app-survey.modal.description")}
        renderDescription={(description) => (
          <div className="flex flex-col items-center">
            <span>{description}</span>
            <Anchor to={t("component.community-post.app-survey.modal.link")}>
              {t("component.community-post.app-survey.modal.link")}
            </Anchor>
          </div>
        )}
        isLoading={false}
        theme="info"
        onResolve={appSurveyModalHandler.setFalse}
        onReject={appSurveyModalHandler.setFalse}
        rejectBtnProps={{
          hide: true,
        }}
        resolveBtnProps={{
          "data-testid": "app-survey-modal-confirm",
          text: t("common.action.ok"),
        }}
        isOpened={isAppSurveyModalOpen}
        shouldCloseOnEsc
        data-testid="cancel-app-survey-modal"
        onOpenChange={appSurveyModalHandler.set}
      />
    </>
  );
}
